import { useCallback, useMemo, useState } from "react";
import type ExcelJS from "exceljs";
import { useTranslation } from "react-i18next";
import { CATEGORY_META } from "../CableTakeoffTabV3/deviceCategories";
import { ANALYTICS_EVENTS, trackEvent } from "../../../../utils/analytics";
import api from "../../../../utils/api";
import type {
  ExportExcelButtonV3Props,
  AggregatedExportDataResponse,
  PerPlanExportDataResponse,
} from "./types";
import {
  roundMeters,
  roundQuantity,
  buildWorkbook,
  addSectionHeader,
  addColumnHeaderRow,
  downloadBlob,
  downloadWorkbook,
  normalizeUnit,
  priceCellsOf,
} from "./utils";
import { addCableSheets, type CableExportLabels } from "./cableExport";
import { excludeArchivedCablePlans } from "../utils/excludeArchivedCablePlans";
import { buildCableExportRows } from "./buildCableExportRows";
import { loadCableExportPlans } from "./loadCableExportPlans";
import { buildCountListRows } from "./buildCountListRows";
import { buildExportGroupValues } from "./buildExportGroupValues";
import { buildGaebX83 } from "./buildGaebX83";
import {
  buildCountListWorkbook,
  type CountListHeaderLabels,
  type FormatCountListPriceValue,
} from "./buildCountListWorkbook";
import { systemMeta } from "../CableTakeoffTabV3/cableSystems";
import { formatPricePropertyValue } from "../OverviewModal/OverviewGrid/DetectedTypeCell/PricePropertyChip/formatPricePropertyValue";
import type { GetItemTypeSettingsResponse } from "../OverviewModal/useItemTypeSettings";
import { hasAnyPrices } from "../OverviewModal/hasAnyPrices";
import { toFolderQuantityColumns } from "../OverviewModal/toFolderQuantityColumns";
import { toPlanQuantityColumns } from "../OverviewModal/toPlanQuantityColumns";
import type { OverviewColumnView } from "../OverviewModal/OverviewGrid";

const SECTION_HEADER_BG = "FFD6E4F7";
const SECTION_HEADER_FONT = "FF003A8C";
const SUBSECTION_HEADER_BG = "FFEFF4FA";
const SUBSECTION_HEADER_FONT = "FF003A8C";

export const useExportHandlers = ({
  projectId,
  projectName,
  isFloorPlanGroupApproved,
  isCableGroupApproved,
  isQuantityGroupApproved,
  canManagePrices,
  cables,
  planRecords,
  boqExtraction,
}: ExportExcelButtonV3Props) => {
  const { t, i18n } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  // Labels for the cable takeoff worksheets
  const cableLabels = useMemo<CableExportLabels>(
    () => ({
      quantitiesSheet: t("smartSurveyor.exportExcel.cableQuantitiesSheet"),
      detailSheet: t("smartSurveyor.exportExcel.cableDetailSheet"),
      systemLabel: (key: string) => {
        const m = systemMeta(key);
        return t(m.labelKey);
      },
      cableType: t("smartSurveyor.exportExcel.cableType"),
      circuits: t("smartSurveyor.exportExcel.circuits"),
      devices: t("smartSurveyor.exportExcel.devices"),
      length: t("smartSurveyor.exportExcel.lengthM"),
      method: t("smartSurveyor.exportExcel.method"),
      plan: t("smartSurveyor.exportExcel.plan"),
      board: t("smartSurveyor.exportExcel.board"),
      circuit: t("smartSurveyor.exportExcel.circuit"),
      measured: t("smartSurveyor.cablesBeta.exportMeasuredM"),
      vertical: t("smartSurveyor.cablesBeta.exportVerticalM"),
      status: t("smartSurveyor.cablesBeta.exportStatus"),
      subtotal: (systemLabel: string) =>
        t("smartSurveyor.exportExcel.cableSubtotal", {
          system: systemLabel,
        }),
      grandTotal: t("smartSurveyor.exportExcel.cableGrandTotal"),
      statusStruck: t("smartSurveyor.cablesBeta.exportStatusStruck"),
      statusEdited: t("smartSurveyor.cablesBeta.exportStatusEdited"),
      statusEditsUnavailable: t(
        "smartSurveyor.cablesBeta.exportStatusEditsUnavailable",
      ),
    }),
    [t],
  );

  // The panel derives its metres from the run's geometry plus the estimator's
  // stored edits, so the workbook has to be built from the same derivation.
  const addEditedCableSheets = useCallback(
    async (workbook: ExcelJS.Workbook) => {
      const exportableCables = excludeArchivedCablePlans({
        cables: cables ?? null,
        planRecords,
      });
      const plans = await loadCableExportPlans({
        projectId,
        cables: exportableCables,
      });
      const { rows } = buildCableExportRows({
        plans,
        categoryName: (category) => t(CATEGORY_META[category].i18nKey),
      });
      addCableSheets(workbook, rows, cableLabels);
    },
    [projectId, cables, planRecords, cableLabels, t],
  );

  const labels = {
    yes: t("smartSurveyor.common.yes"),
    no: t("smartSurveyor.common.no"),
    partial: t("smartSurveyor.common.partial"),
  };

  const sectionLabels = useMemo(
    () => ({
      floorPlanElements: t(
        "smartSurveyor.exportExcel.sectionFloorPlanElements",
      ),
      floorPlanRoutes: t("smartSurveyor.exportExcel.sectionFloorPlanRoutes"),
      diagrams: t("smartSurveyor.exportExcel.sectionDiagrams"),
    }),
    [t],
  );

  const unitLabels = useMemo(
    () => ({
      piece: t("smartSurveyor.exportExcel.unitPiece"),
      meters: t("smartSurveyor.exportExcel.unitMeters"),
    }),
    [t],
  );

  // Same headers as the overview grid, so the sheets read like the table they came from
  const priceHeaderLabels = useMemo(
    () => ({
      materialPrice: t("smartSurveyor.overview.price.columnHeader.material"),
      hours: t("smartSurveyor.overview.price.columnHeader.hours"),
      hourlyRate: t("smartSurveyor.overview.price.columnHeader.hourlyRate"),
      unitPrice: t("smartSurveyor.overview.price.header"),
    }),
    [t],
  );

  const columnHeaders = useMemo(
    () => ({
      sheetName: t("smartSurveyor.exportExcel.sheetName"),
      index: t("smartSurveyor.exportExcel.headerIndex"),
      group: t("smartSurveyor.exportExcel.headerGroup"),
      amount: t("smartSurveyor.exportExcel.headerAmount"),
      unit: t("smartSurveyor.exportExcel.headerUnit"),
      ...priceHeaderLabels,
      lvPosition: t("smartSurveyor.exportExcel.headerLvPosition"),
      approved: t("smartSurveyor.exportExcel.headerApproved"),
    }),
    [t, priceHeaderLabels],
  );

  const countListHeaderLabels = useMemo<CountListHeaderLabels>(
    () => ({
      sheetName: t("smartSurveyor.exportExcel.countListSheetName"),
      positionNumber: t("smartSurveyor.exportExcel.countListHeaderPosition"),
      description: t("smartSurveyor.exportExcel.countListHeaderDescription"),
      detectedType: t("smartSurveyor.exportExcel.countListHeaderDetectedType"),
      unit: t("smartSurveyor.exportExcel.countListHeaderUnit"),
      ...priceHeaderLabels,
      boqQuantity: t("smartSurveyor.exportExcel.countListHeaderBoqQuantity"),
      countedTotal: t("smartSurveyor.exportExcel.countListHeaderCountedTotal"),
      diagrams: t("smartSurveyor.exportExcel.countListHeaderDiagrams"),
    }),
    [t, priceHeaderLabels],
  );

  // Fetched per export like the counts, so a file never carries a stale override or price
  const loadGroupValues = useCallback(async () => {
    const response: GetItemTypeSettingsResponse =
      await api.getQuantitySurveyorItemTypeSettings(projectId);
    return {
      groupValuesOf: buildExportGroupValues({
        settings: response.settings,
        unitLabels,
      }),
      includePrices:
        canManagePrices && hasAnyPrices({ settings: response.settings }),
    };
  }, [projectId, unitLabels, canManagePrices]);

  const formatPriceValue = useCallback<FormatCountListPriceValue>(
    ({ property, value }) =>
      formatPricePropertyValue({ property, value, language: i18n.language, t }),
    [i18n.language, t],
  );

  const approvalLabel = useCallback(
    (ids: number[], checker: (id: number) => boolean) => {
      if (ids.length === 0) return labels.no;
      const approvedCount = ids.filter(checker).length;
      if (approvedCount === ids.length) return labels.yes;
      if (approvedCount === 0) return labels.no;
      return `${labels.partial} (${approvedCount}/${ids.length})`;
    },
    [labels.yes, labels.no, labels.partial],
  );

  const knownPositionNumbers = useMemo(
    () =>
      new Set(
        (boqExtraction?.positions ?? []).map(
          (position) => position.position_number,
        ),
      ),
    [boqExtraction],
  );

  // A stored position the tender file no longer has is unmatched in the grid and the count list, so it is not printed here either
  const resolveLvPosition = useCallback(
    (lvMatchId?: string) =>
      lvMatchId !== undefined && knownPositionNumbers.has(lvMatchId)
        ? lvMatchId
        : "",
    [knownPositionNumbers],
  );

  const handleExportAggregated = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      const [data, { groupValuesOf, includePrices }] = await Promise.all([
        api.getQuantitySurveyorAggregatedExportData(
          projectId,
        ) as Promise<AggregatedExportDataResponse>,
        loadGroupValues(),
      ]);

      const { workbook, sheet } = buildWorkbook(columnHeaders, includePrices);

      if (data.elementGroups.length > 0) {
        addSectionHeader(
          sheet,
          sectionLabels.floorPlanElements,
          SECTION_HEADER_BG,
          SECTION_HEADER_FONT,
        );
        addColumnHeaderRow(sheet, columnHeaders);
        data.elementGroups.forEach((group, idx) => {
          const groupValues = groupValuesOf({
            kind: "element",
            name: group.groupId,
          });
          sheet.addRow({
            Index: idx + 1,
            Group: group.groupId,
            Amount: group.count,
            Unit: groupValues.unit,
            ...priceCellsOf(groupValues),
            LVPosition: resolveLvPosition(group.lvMatchId),
            Approved: approvalLabel(
              group.elementTypeIds,
              isFloorPlanGroupApproved,
            ),
          });
        });
      }

      if (data.cableGroups.length > 0) {
        addSectionHeader(
          sheet,
          sectionLabels.floorPlanRoutes,
          SECTION_HEADER_BG,
          SECTION_HEADER_FONT,
        );
        addColumnHeaderRow(sheet, columnHeaders);
        data.cableGroups.forEach((group, idx) => {
          const groupValues = groupValuesOf({
            kind: "route",
            name: group.label,
          });
          sheet.addRow({
            Index: idx + 1,
            Group: group.label,
            Amount: roundMeters(group.meters),
            Unit: groupValues.unit,
            ...priceCellsOf(groupValues),
            LVPosition: resolveLvPosition(group.lvMatchId),
            Approved: approvalLabel(group.routeTypeIds, isCableGroupApproved),
          });
        });
      }

      if (data.quantityItems.length > 0) {
        addSectionHeader(
          sheet,
          sectionLabels.diagrams,
          SECTION_HEADER_BG,
          SECTION_HEADER_FONT,
        );
        addColumnHeaderRow(sheet, columnHeaders);

        const quantitySourceFiles = new Set(
          data.quantityItems.map((item) => item.sourceFile).filter(Boolean),
        );
        const isMultiFileExport = quantitySourceFiles.size > 1;

        data.quantityItems.forEach((item, idx) => {
          const itemLabel = item.text
            ? `${item.item} — ${item.text}`
            : item.item;
          const groupLabel =
            isMultiFileExport && item.sourceFile
              ? `[${item.sourceFile}] ${itemLabel}`
              : itemLabel;
          sheet.addRow({
            Index: idx + 1,
            Group: groupLabel,
            Amount: roundQuantity(item.totalCount),
            Unit: normalizeUnit(item.unit, unitLabels),
            LVPosition: resolveLvPosition(item.lvMatchId),
            Approved: isQuantityGroupApproved(item.diagramItemTypeId)
              ? labels.yes
              : labels.no,
          });
        });
      }

      await addEditedCableSheets(workbook);

      await downloadWorkbook(workbook, `survey_export_all_${Date.now()}.xlsx`);
      trackEvent(ANALYTICS_EVENTS.EXCEL_EXPORTED, {
        exportVariant: "fullReport",
        floorGroupCount: data.elementGroups.length,
        cableGroupCount: data.cableGroups.length,
        quantityGroupCount: data.quantityItems.length,
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    isExporting,
    projectId,
    columnHeaders,
    sectionLabels,
    unitLabels,
    labels.yes,
    labels.no,
    approvalLabel,
    isFloorPlanGroupApproved,
    isCableGroupApproved,
    isQuantityGroupApproved,
    loadGroupValues,
    addEditedCableSheets,
    resolveLvPosition,
  ]);

  const handleExportPerPlan = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      const [data, { groupValuesOf, includePrices }] = await Promise.all([
        api.getQuantitySurveyorPerPlanExportData(
          projectId,
        ) as Promise<PerPlanExportDataResponse>,
        loadGroupValues(),
      ]);

      const { workbook, sheet } = buildWorkbook(columnHeaders, includePrices);

      for (const plan of data.plans) {
        addSectionHeader(
          sheet,
          plan.planName,
          SECTION_HEADER_BG,
          SECTION_HEADER_FONT,
        );

        if (plan.groups.length > 0) {
          addSectionHeader(
            sheet,
            sectionLabels.floorPlanElements,
            SUBSECTION_HEADER_BG,
            SUBSECTION_HEADER_FONT,
          );
          addColumnHeaderRow(sheet, columnHeaders);
          plan.groups.forEach((group, idx) => {
            const groupValues = groupValuesOf({
              kind: "element",
              name: group.groupId,
            });
            sheet.addRow({
              Index: idx + 1,
              Group: group.groupId,
              Amount: group.count,
              Unit: groupValues.unit,
              ...priceCellsOf(groupValues),
              LVPosition: resolveLvPosition(group.lvMatchId),
              Approved: isFloorPlanGroupApproved(group.elementTypeId)
                ? labels.yes
                : labels.no,
            });
          });
        }

        if (plan.routeGroups.length > 0) {
          addSectionHeader(
            sheet,
            sectionLabels.floorPlanRoutes,
            SUBSECTION_HEADER_BG,
            SUBSECTION_HEADER_FONT,
          );
          addColumnHeaderRow(sheet, columnHeaders);
          plan.routeGroups.forEach((routeGroup, idx) => {
            const groupValues = groupValuesOf({
              kind: "route",
              name: routeGroup.label,
            });
            sheet.addRow({
              Index: idx + 1,
              Group: routeGroup.label,
              Amount: roundMeters(routeGroup.meters),
              Unit: groupValues.unit,
              ...priceCellsOf(groupValues),
              LVPosition: resolveLvPosition(routeGroup.lvMatchId),
              Approved: isCableGroupApproved(routeGroup.routeTypeId)
                ? labels.yes
                : labels.no,
            });
          });
        }
      }

      if (data.diagrams.length > 0) {
        addSectionHeader(
          sheet,
          sectionLabels.diagrams,
          SECTION_HEADER_BG,
          SECTION_HEADER_FONT,
        );

        for (const diagram of data.diagrams) {
          addSectionHeader(
            sheet,
            diagram.diagramName,
            SUBSECTION_HEADER_BG,
            SUBSECTION_HEADER_FONT,
          );
          addColumnHeaderRow(sheet, columnHeaders);

          diagram.itemTypes.forEach((itemType, idx) => {
            const groupLabel = itemType.text
              ? `${itemType.item} — ${itemType.text}`
              : itemType.item;
            sheet.addRow({
              Index: idx + 1,
              Group: groupLabel,
              Amount: roundQuantity(itemType.totalCount),
              Unit: normalizeUnit(itemType.unit, unitLabels),
              LVPosition: resolveLvPosition(itemType.lvMatchId),
              Approved: isQuantityGroupApproved(itemType.diagramItemTypeId)
                ? labels.yes
                : labels.no,
            });
          });
        }
      }

      await addEditedCableSheets(workbook);

      await downloadWorkbook(
        workbook,
        `survey_export_per_plan_${Date.now()}.xlsx`,
      );
      trackEvent(ANALYTICS_EVENTS.EXCEL_EXPORTED, {
        exportVariant: "perPlan",
        planCount: data.plans.length,
        diagramCount: data.diagrams.length,
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    isExporting,
    projectId,
    columnHeaders,
    sectionLabels,
    unitLabels,
    labels.yes,
    labels.no,
    isFloorPlanGroupApproved,
    isCableGroupApproved,
    isQuantityGroupApproved,
    loadGroupValues,
    addEditedCableSheets,
    resolveLvPosition,
  ]);

  // The same sheet as the Overview grid: one quantity column per plan, or per folder summing its plans
  const exportCountList = useCallback(
    async (columnView: OverviewColumnView) => {
      if (isExporting) return;
      setIsExporting(true);

      try {
        const [data, { groupValuesOf, includePrices }] = await Promise.all([
          api.getQuantitySurveyorPerPlanExportData(
            projectId,
          ) as Promise<PerPlanExportDataResponse>,
          loadGroupValues(),
        ]);

        const { planColumns, rows } = buildCountListRows({
          boqExtraction: boqExtraction ?? null,
          perPlanData: data,
          unitLabels,
        });
        const quantityColumns =
          columnView === "folders"
            ? toFolderQuantityColumns({
                planColumns,
                noFolderLabel: t("smartSurveyor.overview.noFolder"),
              })
            : toPlanQuantityColumns({ planColumns });

        const workbook = buildCountListWorkbook({
          quantityColumns,
          rows,
          headerLabels:
            columnView === "folders"
              ? {
                  ...countListHeaderLabels,
                  sheetName: t(
                    "smartSurveyor.exportExcel.folderCountListSheetName",
                  ),
                }
              : countListHeaderLabels,
          groupValuesOf,
          includePrices,
          formatPriceValue,
        });

        await downloadWorkbook(
          workbook,
          columnView === "folders"
            ? `survey_export_count_list_by_folder_${Date.now()}.xlsx`
            : `survey_export_count_list_${Date.now()}.xlsx`,
        );
        trackEvent(ANALYTICS_EVENTS.EXCEL_EXPORTED, {
          exportVariant:
            columnView === "folders" ? "folderCountList" : "countList",
          planCount: data.plans.length,
          ...(columnView === "folders" && {
            folderCount: quantityColumns.length,
          }),
          positionCount: boqExtraction?.positions.length ?? 0,
          unmatchedRowCount: rows.filter((row) => row.kind === "unmatched")
            .length,
        });
      } finally {
        setIsExporting(false);
      }
    },
    [
      isExporting,
      boqExtraction,
      projectId,
      unitLabels,
      countListHeaderLabels,
      loadGroupValues,
      formatPriceValue,
      t,
    ],
  );

  const handleExportCountList = useCallback(async () => {
    await exportCountList("plans");
  }, [exportCountList]);

  const handleExportFolderCountList = useCallback(async () => {
    await exportCountList("folders");
  }, [exportCountList]);

  const handleExportGaebX83 = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      const [data, { groupValuesOf }] = await Promise.all([
        api.getQuantitySurveyorPerPlanExportData(
          projectId,
        ) as Promise<PerPlanExportDataResponse>,
        loadGroupValues(),
      ]);

      const { rows } = buildCountListRows({
        boqExtraction: boqExtraction ?? null,
        perPlanData: data,
        unitLabels,
      });

      const result = buildGaebX83({
        rows,
        groupValuesOf,
        projectName:
          projectName ?? boqExtraction?.project_name ?? "Leistungsverzeichnis",
        labels: {
          elementsSection: sectionLabels.floorPlanElements,
          cablesSection: sectionLabels.floorPlanRoutes,
          diagramsSection: sectionLabels.diagrams,
          unassignedSection: t(
            "smartSurveyor.exportExcel.gaebUnassignedSection",
          ),
        },
      });
      if (!result) return;

      downloadBlob(
        new Blob([result.xml], { type: "application/xml;charset=utf-8" }),
        result.fileName,
      );
      trackEvent(ANALYTICS_EVENTS.GAEB_EXPORTED, {
        positionCount: result.positionCount,
        planCount: data.plans.length,
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    isExporting,
    projectId,
    projectName,
    boqExtraction,
    unitLabels,
    sectionLabels,
    loadGroupValues,
    t,
  ]);

  return {
    handleExportAggregated,
    handleExportPerPlan,
    handleExportCountList,
    handleExportFolderCountList,
    handleExportGaebX83,
    isExporting,
  };
};
